/**
 * Evaluations Page
 * Mobile-responsive main page for viewing and managing evaluations with role-based access
 * Optimized for mobile devices, tablets, and desktops with touch-friendly interfaces
 */

import React, { useState, useEffect, useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { MdVisibility, MdEdit, MdDelete } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Badge } from '../../components/common/Badge';
import { Button } from '../../components/common/Button';
import { Card, CardContent } from '../../components/common/Card';
import { ConfirmationModal } from '../../components/common/ConfirmationModal';
import { Pagination, PaginationMeta } from '../../components/common/Pagination';
import { StatsCard } from '../../components/shared/StatsCard';
import { ROUTES } from '../../constants';
import {
  evaluationService,
  EvaluationSummary,
  EvaluationFilters as FilterType,
  EvaluationStats,
} from '../../services/evaluationService';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { cn, formatDate } from '../../utils';

interface EvaluationsPageProps {
  className?: string;
}

interface SearchForm {
  search: string;
  status_filter: string;
  evaluation_period: string;
}

// Helper function to map evaluation status to Badge variant
const getStatusBadgeConfig = (status: string) => {
  switch (status) {
    case 'completed':
      return { variant: 'success' as const, label: 'Completed' };
    case 'approved':
      return { variant: 'success' as const, label: 'Approved' };
    case 'in_progress':
      return { variant: 'info' as const, label: 'In Progress' };
    case 'pending':
      return { variant: 'info' as const, label: 'Pending' };
    case 'scored':
      return { variant: 'info' as const, label: 'Scored' };
    case 'draft':
      return { variant: 'default' as const, label: 'Draft' };
    case 'archived':
      return { variant: 'danger' as const, label: 'Archived' };
    default:
      return { variant: 'default' as const, label: status };
  }
};

// Inline Evaluation Card Component
const EvaluationCard: React.FC<{
  evaluation: EvaluationSummary;
  isTeacher: boolean;
  isAdmin: boolean;
  onView?: (evaluationId: string) => void;
  onEdit?: (evaluationId: string) => void;
  onDelete?: (evaluation: EvaluationSummary) => void;
}> = ({ evaluation, isTeacher, isAdmin, onView, onEdit, onDelete }) => {
  return (
    <Card className="hover:shadow-md transition-all duration-200 cursor-pointer group hover:border-l-4 hover:border-l-blue-500">
      <CardContent className="p-0">
        {/* Header with title and actions */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 truncate">
              {isTeacher
                ? `Evaluation ${evaluation.id.slice(-8)}`
                : `Teacher: ${evaluation.teacher?.full_name || evaluation.teacher_name || evaluation.teacher_id}`}
            </h3>
            <div className="flex items-center gap-2 mt-2">
              {(() => {
                const config = getStatusBadgeConfig(evaluation.status);
                return (
                  <Badge variant={config.variant} size="sm">
                    {config.label}
                  </Badge>
                );
              })()}
              {(isAdmin || isTeacher) && evaluation.status === 'draft' && (
                <Badge variant="warning" size="sm">
                  Editable
                </Badge>
              )}
            </div>
          </div>

          {/* Action buttons - fixed width container */}
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            {/* Primary action button */}
            <Button
              variant="icon-primary"
              size="sm"
              onClick={() => onView?.(evaluation.id)}
              title={
                ['draft', 'in_progress', 'pending'].includes(evaluation.status)
                  ? 'Continue'
                  : 'View Details'
              }
            >
              {React.createElement(MdVisibility as React.ElementType, {
                className: 'w-5 h-5',
              })}
            </Button>

            {/* Admin actions for draft evaluations */}
            {isAdmin && evaluation.status === 'draft' && (
              <>
                <Button
                  variant="icon-secondary"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit?.(evaluation.id);
                  }}
                  title="Edit"
                >
                  {React.createElement(MdEdit as React.ElementType, {
                    className: 'w-5 h-5',
                  })}
                </Button>
                <Button
                  variant="icon-danger"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    onDelete?.(evaluation);
                  }}
                  title="Delete"
                >
                  {React.createElement(MdDelete as React.ElementType, {
                    className: 'w-5 h-5',
                  })}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Three-column details section - full width */}
        <div className="flex flex-col sm:flex-row gap-4 text-sm text-gray-600 mt-4 sm:mt-8">
          <div className="flex items-start gap-3 w-full sm:w-1/3">
            <span className="text-gray-400 flex-shrink-0 mt-0.5 text-base">
              📅
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-700 mb-1">Period</div>
              <div className="text-gray-600 break-words">
                {evaluation.evaluation_period}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 w-full sm:w-1/3">
            <span className="text-gray-400 flex-shrink-0 mt-0.5 text-base">
              📋
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-700 mb-1">Template</div>
              <div className="text-gray-600 break-words">
                {evaluation.template?.name ||
                  evaluation.template_id ||
                  evaluation.rubric_template_id}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 w-full sm:w-1/3">
            <span className="text-gray-400 flex-shrink-0 mt-0.5 text-base">
              📆
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-700 mb-1">Created</div>
              <div className="text-gray-600 break-words">
                {formatDate(evaluation.created_at)}
              </div>
            </div>
          </div>
        </div>

        {/* Performance indicators */}
        {(evaluation.performance_level ||
          evaluation.percentage_score !== undefined) && (
          <div className="flex flex-wrap gap-2 mt-4">
            {evaluation.performance_level && (
              <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium bg-blue-100 text-blue-800">
                <span className="mr-1">🎯</span>
                Performance:{' '}
                {evaluation.performance_level.replace('_', ' ').toUpperCase()}
              </span>
            )}
            {evaluation.percentage_score !== undefined &&
              evaluation.percentage_score !== null && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  <span className="mr-1">📈</span>
                  Score: {evaluation.percentage_score.toFixed(1)}%
                </span>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const EvaluationsPage: React.FC<EvaluationsPageProps> = ({
  className,
}) => {
  const { user } = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [evaluations, setEvaluations] = useState<EvaluationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<EvaluationStats | null>(null);
  const [deletingEvaluation, setDeletingEvaluation] =
    useState<EvaluationSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    per_page: 10,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    next_page: undefined,
    prev_page: undefined,
  });
  const [itemsPerPage] = useState(10); // 10 items per page

  // Get current filters from URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const urlSearchTerm = searchParams.get('search') || '';
  const urlStatusFilter = searchParams.get('status') || '';
  const urlPeriodFilter = searchParams.get('period') || '';

  // Search form
  const { register, handleSubmit, reset } = useForm<SearchForm>({
    defaultValues: {
      search: urlSearchTerm,
      status_filter: urlStatusFilter,
      evaluation_period: urlPeriodFilter,
    },
  });

  // Role-based access control
  const isTeacher = user?.role === UserRole.TEACHER;
  const isAdmin = [
    UserRole.SUPER_ADMIN,
    UserRole.DISTRICT_ADMIN,
    UserRole.SCHOOL_ADMIN,
    UserRole.PRINCIPAL,
    UserRole.ASSISTANT_PRINCIPAL,
    UserRole.EVALUATOR,
  ].includes(user?.role as UserRole);

  // Load stats from API
  const loadStats = useCallback(async () => {
    try {
      const teacherId = isTeacher ? user?.id?.toString() : undefined;
      const statsData = await evaluationService.getEvaluationStats(teacherId);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load stats:', err);
      // Don't set error state for stats failure, just log it
    }
  }, [isTeacher, user?.id]);

  // Load evaluations with proper error handling
  const loadEvaluations = useCallback(
    async (
      page: number = currentPage,
      search: string = urlSearchTerm,
      status: string = urlStatusFilter,
      period: string = urlPeriodFilter
    ) => {
      try {
        setLoading(true);
        setError(null);

        // Build filters from URL parameters
        const filters: FilterType = {
          skip: (page - 1) * itemsPerPage, // Convert page to skip
          limit: itemsPerPage,
        };

        // Add teacher filter for teachers
        if (isTeacher) {
          filters.teacher_id = user?.id?.toString();
        }

        // Add search filters if provided
        if (search.trim()) {
          filters.search = search.trim();
        }
        if (status) {
          filters.status_filter = status;
        }
        if (period) {
          filters.evaluation_period = period;
        }

        const response = await evaluationService.getEvaluations(filters);

        if (response) {
          setEvaluations(response.data || []);
          setPagination({
            page: response.pagination.page || 1,
            per_page: response.pagination.per_page || itemsPerPage,
            total: response.pagination.total || 0,
            pages: response.pagination.pages || 1,
            has_next: response.pagination.has_next || false,
            has_prev: response.pagination.has_prev || false,
            next_page: response.pagination.next_page || undefined,
            prev_page: response.pagination.prev_page || undefined,
          });
        }
      } catch (err) {
        console.error('Failed to load evaluations:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load evaluations'
        );
        setEvaluations([]);
        setPagination({
          page: 1,
          per_page: itemsPerPage,
          total: 0,
          pages: 1,
          has_next: false,
          has_prev: false,
          next_page: undefined,
          prev_page: undefined,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      isTeacher,
      user?.id,
      currentPage,
      urlSearchTerm,
      urlStatusFilter,
      urlPeriodFilter,
      itemsPerPage,
    ]
  );

  useEffect(() => {
    loadEvaluations();
    loadStats();
  }, [loadEvaluations, loadStats]);

  // Event handlers
  const handleCreateEvaluation = () => {
    navigate(ROUTES.EVALUATIONS_CREATE);
  };

  const handleViewEvaluation = (evaluationId: string) => {
    navigate(ROUTES.EVALUATION_DETAIL.replace(':id', evaluationId));
  };

  const handleEditEvaluation = (evaluationId: string) => {
    navigate(`${ROUTES.EVALUATIONS_CREATE}?mode=edit&id=${evaluationId}`);
  };

  const handleDeleteEvaluation = (evaluation: EvaluationSummary) => {
    setDeletingEvaluation(evaluation);
  };

  const confirmDelete = async () => {
    if (!deletingEvaluation) return;

    try {
      setIsDeleting(true);
      await evaluationService.deleteEvaluation(deletingEvaluation.id);
      await loadEvaluations(); // Refresh the list
      setDeletingEvaluation(null);
    } catch (err) {
      console.error('Failed to delete evaluation:', err);
      setError('Failed to delete evaluation');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle search form submission
  const handleSearch = (data: SearchForm) => {
    const newSearchParams = new URLSearchParams(searchParams);

    // Update search parameters
    if (data.search.trim()) {
      newSearchParams.set('search', data.search.trim());
    } else {
      newSearchParams.delete('search');
    }

    if (data.status_filter) {
      newSearchParams.set('status', data.status_filter);
    } else {
      newSearchParams.delete('status');
    }

    if (data.evaluation_period) {
      newSearchParams.set('period', data.evaluation_period);
    } else {
      newSearchParams.delete('period');
    }

    newSearchParams.set('page', '1'); // Reset to first page when searching
    setSearchParams(newSearchParams);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', page.toString());
      setSearchParams(newSearchParams);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
    reset({ search: '', status_filter: '', evaluation_period: '' });
  };

  // Loading skeleton components
  const LoadingSkeleton = () => (
    <Card className="animate-pulse">
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 space-y-2 sm:space-y-0">
              <div className="h-5 bg-gray-200 rounded w-48"></div>
              <div className="h-6 bg-gray-200 rounded-full w-20"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              <div className="h-4 bg-gray-200 rounded w-32"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
              <div className="h-4 bg-gray-200 rounded w-28"></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="h-6 bg-gray-200 rounded-full w-24"></div>
              <div className="h-6 bg-gray-200 rounded-full w-20"></div>
            </div>
          </div>
          <div className="flex-shrink-0 sm:ml-6 flex flex-col sm:flex-row gap-2">
            <div className="h-10 bg-gray-200 rounded w-24"></div>
            <div className="h-10 bg-gray-200 rounded w-16"></div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const LoadingState = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
      </div>

      {/* Statistics skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-12"></div>
                </div>
                <div className="p-3 rounded-full bg-gray-200">
                  <div className="w-6 h-6 bg-gray-300 rounded"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters skeleton */}
      <Card className="animate-pulse">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                <div className="h-11 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Evaluation cards skeleton */}
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <LoadingSkeleton key={i} />
        ))}
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <LoadingState />
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {isTeacher ? 'My Evaluations' : 'Teacher Evaluations'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isTeacher
              ? 'View and manage your evaluation submissions'
              : 'Review and manage teacher evaluations'}
          </p>
        </div>

        {(isAdmin || isTeacher) && (
          <Button
            variant="primary"
            onClick={handleCreateEvaluation}
            className="w-full sm:w-auto min-h-[44px] flex-shrink-0"
          >
            <span className="sm:hidden">
              {isTeacher ? 'Submit Video' : 'Create Evaluation'}
            </span>
            <span className="hidden sm:inline">
              {isTeacher ? 'Submit New Video' : 'Create Evaluation'}
            </span>
          </Button>
        )}
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {/* Overview Cards */}
          <StatsCard
            title="Total Evaluations"
            value={stats.total_evaluations}
            icon="assignment"
            color="blue"
            subtitle={`${stats.total_evaluations} total`}
          />
          <StatsCard
            title="Completed"
            value={stats.completed_evaluations}
            icon="check"
            color="green"
            subtitle={`${stats.completion_rate.toFixed(1)}% completion rate`}
          />

          {/* Status Distribution Cards */}
          <StatsCard
            title="Draft"
            value={stats.status_distribution['draft'] || 0}
            icon="draft"
            color="gray"
            subtitle="In progress"
          />
          <StatsCard
            title="Pending"
            value={stats.status_distribution['pending'] || 0}
            icon="progress"
            color="amber"
            subtitle="Awaiting start"
          />
          <StatsCard
            title="In Progress"
            value={stats.status_distribution['in_progress'] || 0}
            icon="progress"
            color="blue"
            subtitle="Currently active"
          />
          <StatsCard
            title="Scored"
            value={stats.status_distribution['scored'] || 0}
            icon="assignment"
            color="indigo"
            subtitle="Awaiting review"
          />
          <StatsCard
            title="Approved"
            value={stats.status_distribution['approved'] || 0}
            icon="check"
            color="green"
            subtitle="Finalized"
          />
          <StatsCard
            title="Archived"
            value={stats.status_distribution['archived'] || 0}
            icon="assignment"
            color="red"
            subtitle="Archived evaluations"
          />

          {/* Score Statistics Cards */}
          {stats.score_statistics.count > 0 && (
            <>
              <StatsCard
                title="Average Score"
                value={stats.score_statistics.average_score.toFixed(1)}
                icon="trending"
                color="green"
                subtitle={`Based on ${stats.score_statistics.count} evaluations`}
              />
              <StatsCard
                title="Highest Score"
                value={stats.score_statistics.max_score}
                icon="trending"
                color="amber"
                subtitle="Best performance"
              />
              <StatsCard
                title="Lowest Score"
                value={stats.score_statistics.min_score}
                icon="trending"
                color="red"
                subtitle="Needs improvement"
              />
            </>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent>
          <form
            onSubmit={handleSubmit(handleSearch)}
            className="space-y-4 lg:space-y-0"
          >
            {/* Mobile/Tablet Layout - Two Rows */}
            <div className="flex flex-col space-y-4 lg:hidden">
              {/* Row 1: Search Input and Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <input
                    {...register('search')}
                    type="text"
                    placeholder={
                      isTeacher
                        ? 'Search your evaluations...'
                        : 'Search by teacher name, template name...'
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                    defaultValue={urlSearchTerm}
                  />
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading}
                    className="min-h-[44px] flex-1 sm:flex-none sm:px-6"
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                  {(urlSearchTerm || urlStatusFilter || urlPeriodFilter) && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={clearFilters}
                      className="min-h-[44px] px-4"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Row 2: Filter Dropdowns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="status-filter-mobile"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Status
                  </label>
                  <select
                    {...register('status_filter')}
                    id="status-filter-mobile"
                    className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="scored">Scored</option>
                    <option value="completed">Completed</option>
                    <option value="approved">Approved</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="period-filter-mobile"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Evaluation Period
                  </label>
                  <select
                    {...register('evaluation_period')}
                    id="period-filter-mobile"
                    className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  >
                    <option value="">All Periods</option>
                    {(() => {
                      const currentYear = new Date().getFullYear();
                      const periods = [];
                      for (let i = -5; i <= 5; i++) {
                        const year = currentYear + i;
                        const nextYear = year + 1;
                        periods.push(
                          <option key={year} value={`${year}-${nextYear}`}>
                            {year}-{nextYear}
                          </option>
                        );
                      }
                      return periods;
                    })()}
                  </select>
                </div>
              </div>
            </div>

            {/* Desktop Layout - All Inline */}
            <div className="hidden lg:flex lg:items-end lg:gap-4">
              <div className="flex-1 min-w-0">
                {/* <label
                  htmlFor="search-desktop"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Search
                </label> */}
                <input
                  {...register('search')}
                  id="search-desktop"
                  type="text"
                  placeholder={
                    isTeacher
                      ? 'Search your evaluations...'
                      : 'Search by teacher name, template name...'
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                  defaultValue={urlSearchTerm}
                />
              </div>

              <div className="w-48">
                {/* <label
                  htmlFor="status-filter-desktop"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Status
                </label> */}
                <select
                  {...register('status_filter')}
                  id="status-filter-desktop"
                  className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="scored">Scored</option>
                  <option value="completed">Completed</option>
                  <option value="approved">Approved</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div className="w-48">
                {/* <label
                  htmlFor="period-filter-desktop"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Period
                </label> */}
                <select
                  {...register('evaluation_period')}
                  id="period-filter-desktop"
                  className="w-full px-3 py-2 min-h-[44px] border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                >
                  <option value="">All Periods</option>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const periods = [];
                    for (let i = -5; i <= 5; i++) {
                      const year = currentYear + i;
                      const nextYear = year + 1;
                      periods.push(
                        <option key={year} value={`${year}-${nextYear}`}>
                          {year}-{nextYear}
                        </option>
                      );
                    }
                    return periods;
                  })()}
                </select>
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="min-h-[44px] px-6"
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
                {(urlSearchTerm || urlStatusFilter || urlPeriodFilter) && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={clearFilters}
                    className="min-h-[44px] px-4"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-8">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-12 h-12 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error Loading Evaluations
              </h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button
                variant="primary"
                onClick={() => loadEvaluations()}
                className="min-h-[44px]"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Evaluations List */}
      {!error && evaluations.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {isTeacher ? 'No evaluations yet' : 'No evaluations found'}
              </h3>
              <p className="text-gray-600 mb-6">
                {(() => {
                  const hasActiveFilters =
                    urlSearchTerm || urlStatusFilter || urlPeriodFilter;

                  if (isTeacher) {
                    return 'Get started by submitting your first lesson video for evaluation.';
                  } else if (hasActiveFilters) {
                    return 'No evaluations match your current filters. Try adjusting your search criteria.';
                  } else {
                    return 'No evaluations have been created yet. Create your first evaluation to get started.';
                  }
                })()}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {isTeacher ? (
                  <Button
                    variant="primary"
                    onClick={handleCreateEvaluation}
                    className="min-h-[44px]"
                  >
                    Submit First Video
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      onClick={handleCreateEvaluation}
                      className="min-h-[44px]"
                    >
                      Create Evaluation
                    </Button>
                    {(urlSearchTerm || urlStatusFilter || urlPeriodFilter) && (
                      <Button
                        variant="secondary"
                        onClick={clearFilters}
                        className="min-h-[44px]"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {evaluations.map(evaluation => (
            <EvaluationCard
              key={evaluation.id}
              evaluation={evaluation}
              isTeacher={isTeacher}
              isAdmin={isAdmin}
              onView={handleViewEvaluation}
              onEdit={handleEditEvaluation}
              onDelete={handleDeleteEvaluation}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && !error && (
        <div className="bg-white px-4 py-3 border border-gray-200 rounded-lg">
          <Pagination pagination={pagination} onPageChange={handlePageChange} />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deletingEvaluation}
        onClose={() => setDeletingEvaluation(null)}
        onConfirm={confirmDelete}
        title="Delete Evaluation"
        message={`Are you sure you want to delete this evaluation? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};
